import * as React from 'react';
import { Building2 } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import {
  ESTABLISHMENT_CATEGORY_FORM_OPTIONS,
  ESTABLISHMENT_PRIORITY_NETWORK_FORM_OPTIONS,
  ESTABLISHMENT_STATUS_FORM_OPTIONS,
  ESTABLISHMENT_TYPE_FORM_OPTIONS,
} from '@/components/establishments/establishment-configs';
import type {
  EstablishmentFormFieldValidation,
  EstablishmentFormMode,
  EstablishmentFormState,
} from '@/types/establishment.types';

/** Props for {@link EstablishmentGeneralSection}. */
interface Props {
  mode: EstablishmentFormMode;
  form: EstablishmentFormState;
  onFieldChange: (field: string, value: unknown) => void;
  onBlur: (field: string) => void;
  validation: EstablishmentFormFieldValidation;
}

/** Name, type, status, category, and priority network. */
export function EstablishmentGeneralSection({
  mode,
  form,
  onFieldChange,
  onBlur,
  validation,
}: Readonly<Props>) {
  function handleUaiChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('uaiCode', e.target.value);
  }
  function handleUaiBlur(): void {
    onBlur('uaiCode');
  }
  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('name', e.target.value);
  }
  function handleNameBlur(): void {
    onBlur('name');
  }
  function handleTypeChange(value: string): void {
    onFieldChange('type', value);
  }
  function handleTypeBlur(): void {
    onBlur('type');
  }
  function handleStatusChange(value: string): void {
    onFieldChange('status', value);
  }
  function handleStatusBlur(): void {
    onBlur('status');
  }
  function handleCategoryChange(value: string): void {
    onFieldChange('establiCategory', value);
  }
  function handlePriorityNetworkChange(value: string): void {
    onFieldChange('priorityNetwork', value);
  }
  function handleIpsChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('ipsValue', e.target.value);
  }

  const uaiDisabled = mode === 'edit';

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Building2 className="w-4 h-4" />
        Informations générales
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Code UAI"
          value={form.uaiCode}
          onChange={handleUaiChange}
          onBlur={handleUaiBlur}
          disabled={uaiDisabled}
          placeholder="Ex: 0010001A"
          maxLength={8}
          helperText={
            uaiDisabled
              ? "Le code UAI ne peut pas être modifié car il sert d'identifiant unique"
              : "Code officiel de l'établissement (7 chiffres + 1 lettre, optionnel)"
          }
          state={uaiDisabled ? undefined : validation.getFieldState('uaiCode')}
          error={uaiDisabled ? undefined : validation.getFieldError('uaiCode')}
          inputSize="sm"
        />
        <Input
          label="Nom"
          value={form.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          required
          placeholder="Ex: Collège Jean Moulin"
          maxLength={255}
          state={validation.getFieldState('name')}
          error={validation.getFieldError('name')}
          inputSize="sm"
        />
        <Select
          label="Type"
          value={form.type}
          onChange={handleTypeChange}
          onBlur={handleTypeBlur}
          options={ESTABLISHMENT_TYPE_FORM_OPTIONS}
          placeholder="Sélectionner un type"
          required
          state={validation.getFieldState('type')}
          error={validation.getFieldError('type')}
          size="sm"
        />
        <Select
          label="Statut"
          value={form.status}
          onChange={handleStatusChange}
          onBlur={handleStatusBlur}
          options={ESTABLISHMENT_STATUS_FORM_OPTIONS}
          state={validation.getFieldState('status')}
          error={validation.getFieldError('status')}
          size="sm"
        />
        <Select
          label="Catégorie établissement"
          value={form.establiCategory}
          onChange={handleCategoryChange}
          options={ESTABLISHMENT_CATEGORY_FORM_OPTIONS}
          placeholder="Sélectionner une catégorie"
          size="sm"
        />
        <Select
          label="Réseau d'éducation prioritaire"
          value={form.priorityNetwork}
          onChange={handlePriorityNetworkChange}
          options={ESTABLISHMENT_PRIORITY_NETWORK_FORM_OPTIONS}
          placeholder="Sélectionner un réseau"
          size="sm"
        />
        <Input
          label="IPS (Indice de Position Sociale)"
          type="number"
          value={form.ipsValue}
          onChange={handleIpsChange}
          placeholder="Ex: 95.30"
          min={0}
          max={200}
          step={0.01}
          helperText="Indice de position sociale de l'établissement (API Éducation Nationale)"
          inputSize="sm"
        />
      </div>
    </div>
  );
}
